import _ from 'underscore'

import { Trace } from '../../domain/Trace'

interface TracesFilterResult {
    traces: Trace[]
    hidden: number
    hiddenThreshold: number
}

interface SpanDimensions {
    start: number
    duration: number
}

class TimelineTraceDataTransformer {
    /**
     * Align child traces in the given trace set so the child trace is centered below it's parent trace's root span.
     */
    public alignChildTracesToParentSpans(traces: Trace[]): Trace[] {
        const rootTrace = traces[0]
        const childTraces = traces.slice(1)

        let spanDimensions = this.extractSpanDimensionsFromTrace(rootTrace)

        for (const childTrace of childTraces) {
            const childTraceOffset = this.calculateTraceOffset(childTrace, spanDimensions)

            for (const childTraceSpan of childTrace.spans) {
                childTraceSpan.cs += childTraceOffset

                if (childTraceSpan.e) {
                    for (const localEvent of childTraceSpan.e) {
                        localEvent.cs += childTraceOffset
                    }
                }
            }

            for (const globalEvent of childTrace.globalEvents) {
                globalEvent.ts += childTraceOffset
            }

            /*
             * We need to extract offsets trace by trace because there might be multiple levels of nesting
             * which means a trace's offsets are only final after it has been aligned itself.
             */
            spanDimensions = { ...spanDimensions, ...this.extractSpanDimensionsFromTrace(childTrace) }
        }

        return traces
    }

    public countTraceSpans(traces: Trace[]): number {
        return traces.reduce((spanCount, trace) => spanCount + trace.spans.length, 0)
    }

    public filterFastSpansFromTraces(traces: Trace[], limit: number = 500): TracesFilterResult {
        const initialSpanCount = this.countTraceSpans(traces)

        let threshold = 75
        let runs = 0
        let spanCount = initialSpanCount

        const wallTimeThresholdFilter = span => span.wt >= threshold

        while (spanCount > limit && runs < 5) {
            for (const trace of traces) {
                trace.spans = trace.spans.filter(wallTimeThresholdFilter)
            }

            runs++
            threshold = threshold * 4
            spanCount = this.countTraceSpans(traces)
        }

        return { traces, hidden: initialSpanCount - spanCount, hiddenThreshold: threshold }
    }

    public sortTracesByStartTime(traces: Trace[]): void {
        traces.sort((a, b) => {
            const aStart = a.spans[0].cs
            const bStart = b.spans[0].cs

            if (aStart < bStart) {
                return -1
            }

            if (aStart > bStart) {
                return 1
            }

            return 0
        })
    }

    public cloneTraceSpanAndEventData(traces) {
        return traces.map(trace => {
            const clonedTrace = _.clone(trace)

            clonedTrace.spans = trace.spans.map(span => {
                const clonedSpan = _.clone(span)

                clonedSpan.e = span.e.map(localEvent => _.clone(localEvent))

                return clonedSpan
            })

            clonedTrace.globalEvents = trace.globalEvents.map(_.clone)

            return clonedTrace
        })
    }

    /**
     * Find the correct time offset for the given trace based on the given set of potential parent span dimensions
     */
    private calculateTraceOffset(trace: Trace, spanDimensions: { [key: string]: SpanDimensions }): number {
        if (!trace.parentSpanId) {
            return 0
        }

        const childTraceRootSpan = trace.spans[0]
        const parentSpanDimensions = spanDimensions[trace.parentSpanId]

        const durationDifference = Math.max(parentSpanDimensions.duration - childTraceRootSpan.wt, 0)

        return parentSpanDimensions.start + durationDifference / 2
    }

    private extractSpanDimensionsFromTrace(trace: Trace): { [key: string]: SpanDimensions } {
        const spanDimensions = {}

        for (const span of trace.spans) {
            if (span.spanId) {
                spanDimensions[span.spanId] = {
                    start: span.cs,
                    duration: span.wt,
                }
            }
        }

        return spanDimensions
    }
}

export default TimelineTraceDataTransformer
